"use client";

import Auth from "./Auth";
import { ViewType } from "./types";
import { useSupabase } from "@/app/supabase-provider";
import type { PasswordLoginMode } from "@/components/auth-ui/types";
import { cn } from "@/lib/utils";
import { getAndClear } from "@/utils/auth-error";
import { buildAuthURL } from "@/utils/helpers";
import { useSearchParams } from "next/navigation";
import * as React from "react";

const ACTION_TEXTS: Record<ViewType, string> = {
  sign_up: "Create your account",
  sign_in: "Sign into Listen",
  verify_otp: "Verify your email",
  magic_link: "Get a magic link",
  forgotten_password: "Reset your password",
  update_password: "Update password",
  sso: "Enterprise SSO",
  mfa_challenge: "Two-factor authentication",
};

export function AuthUI({
  view,
  setView,
  passwordLoginMode = "turnstile",
}: {
  view: ViewType;
  setView: (view: ViewType) => void;
  passwordLoginMode?: PasswordLoginMode;
}) {
  const { supabase } = useSupabase();

  const searchParams = useSearchParams();
  const isCreatingPassword =
    view === "update_password" && searchParams.get("intent") === "signup";
  const skipBeforeUserCreateHookKey =
    searchParams.get("access_key") || undefined;
  const isSkipBeforeUserCreateHookMode = !!skipBeforeUserCreateHookKey;
  const providers: Array<"google" | "azure"> =
    isSkipBeforeUserCreateHookMode && view === "magic_link"
      ? []
      : ["google", "azure"];

  const redirectTo = buildAuthURL({
    path: `/auth/callback?${getAndClear(searchParams)}`,
  });
  const titleElement = (
    <h1
      className={cn(
        "text-center text-3xl font-normal leading-[38px]",
        view === "sign_up" ? "text-blue-500" : "text-content-primary"
      )}
    >
      {isCreatingPassword ? "Create password" : ACTION_TEXTS[view]}
    </h1>
  );
  return (
    <Auth
      title={titleElement}
      supabaseClient={supabase}
      passwordLoginMode={passwordLoginMode}
      view={view}
      setAuthView={setView}
      providers={providers}
      providerScopes={{ azure: "email" }}
      redirectTo={redirectTo}
      showLinks={!isSkipBeforeUserCreateHookMode}
      skipBeforeUserCreateHookKey={skipBeforeUserCreateHookKey}
      localization={{
        variables: {
          ...(isCreatingPassword && {
            update_password: {
              password_label: "Password",
              password_input_placeholder: "Your password",
              button_label: "Create password",
              loading_button_label: "Creating password...",
              confirmation_text: "Your password has been created",
            },
          }),
          sign_up: {
            confirmation_text:
              "Check your email for a confirmation link and open the link in this browser.",
          },
        },
      }}
      theme="light"
    />
  );
}
